import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import cx from 'classnames';

import { TextField, MenuItem, Button, Grid, Tabs } from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { StyledTab, TabPanel } from 'components/shared/MuiExtensions';
import Stack from 'components/shared/Stack';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import {
  apiInvalidateLead,
  apiQualifyLead,
  apiPatchLead,
  apiReopenLead,
  apiUnqualifyLead,
  updateLeadSummaryStatus,
} from 'state/lead.state';
import { setNotification } from 'state/app.state';
import { RootState } from 'state/store';
import { DropDowns } from 'utils/constants';
import { buildLeadComment, storeInSessionStorage, getFromSessionStorage } from 'utils/functions';
import { LeadStatus, LeadTag, UnqualifyReason } from 'typings';

const FormContainer = styled('form')`
  display: flex;
  flex-direction: column;
`;

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    maxWidth: '50%',
  },
  formInput: {
    fontSize: 'var(--s-1)',
    height: '2.4em',
  },
  textArea: {
    padding: theme.spacing(1),
  },
  tabRoot: {
    padding: 0,
    minHeight: 0,
  },
  tabIndicator: {
    display: 'none',
  },
  tab: {
    textTransform: 'none',
    height: '2.8em',
    fontSize: 'var(--s-2)',
    margin: 0,
    minWidth: 100,
    whiteSpace: 'nowrap',
  },
}));

const LeadSummary = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const classes = useStyles();
  const theme = useTheme();
  const isMarkedDone = useSelector((state: RootState) => state.lead.isMarkedDone);
  const { leadStatus, leadTag, leadComment, additionalNote, leadReasonUnqualified } = useSelector(
    (state: RootState) => ({
      leadTag: state.lead.entity?.tag,
      leadStatus: state.lead.entity?.status,
      leadComment: state.lead.entity?.leadComment || undefined,
      additionalNote: state.lead.entity?.additionalNote || undefined,
      leadReasonUnqualified: state.lead.entity?.reasonUnqualified || undefined,
    }),
    shallowEqual
  );

  const unqualifyReasons = useSelector(
    (state: RootState) => state.lead.unqualifyReasons,
    (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b)
  );
  const {
    hasQualifyAnswer,
    scriptResponseLeadStatus,
    scriptResponseUnqualifiedReason,
    scriptResponseSummaryString,
  } = useSelector(
    (state: RootState) => ({
      hasQualifyAnswer: state.script.hasQualifyAnswer,
      scriptResponseLeadStatus: state.script.scriptResponseLeadStatus,
      scriptResponseUnqualifiedReason: state.script.scriptResponseUnqualifiedReason,
      scriptResponseSummaryString: state.script.scriptResponseSummaryString,
    }),
    shallowEqual
  );
  const { isNewDistribution } = useSelector(
    (state: RootState) => ({
      isNewDistribution: state.concierge.isNewDistribution,
    }),
    shallowEqual
  );

  const scriptSummaryText = leadComment || '';
  const dispatch = useDispatch();
  const { handleSubmit, control, setValue, formState, watch, getValues, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      status: leadStatus || '',
      reasonUnqualified: scriptResponseUnqualifiedReason || leadReasonUnqualified || '',
      leadComment: scriptSummaryText,
      additionalNote: getFromSessionStorage('additionalNote') || additionalNote || '',
    },
    shouldUnregister: false,
  });

  const unqualifyReasonsString = JSON.stringify(unqualifyReasons);
  const { dirtyFields, isDirty, isSubmitting } = formState;

  const {
    status,
    reasonUnqualified,
    additionalNote: additionalNoteText,
  } = watch(['status', 'reasonUnqualified', 'additionalNote'], {
    status: leadStatus || '',
    reasonUnqualified: scriptResponseUnqualifiedReason || leadReasonUnqualified || '',
  });

  // clear session storage on new distribution
  useEffect(() => {
    if (isNewDistribution) {
      sessionStorage.removeItem('additionalNote');
    }
  }, [isNewDistribution]);

  // reset specific form values after successful api request
  useEffect(() => {
    reset({
      status: leadStatus,
      reasonUnqualified: leadReasonUnqualified ?? '',
      leadComment: leadComment ?? '',
      additionalNote: getFromSessionStorage('additionalNote') ?? additionalNote ?? '',
    });
  }, [leadStatus, leadReasonUnqualified, leadComment, additionalNote, reset]);

  // update form when lead status is derived from script response
  useEffect(() => {
    if (scriptResponseLeadStatus) {
      setValue('status', scriptResponseLeadStatus, { shouldDirty: true });
    }
  }, [scriptResponseLeadStatus, setValue]);

  // update unqualify reason dropdown when a reason is derived from script response
  useEffect(() => {
    if (scriptResponseUnqualifiedReason) {
      setValue('reasonUnqualified', scriptResponseUnqualifiedReason, { shouldDirty: true });
    }
  }, [scriptResponseUnqualifiedReason, setValue]);

  // update form comment when status, reason, or script summary are changed
  useEffect(() => {
    const chosenReason = getValues('reasonUnqualified');
    const parsedUnqualifyReasons: UnqualifyReason[] = JSON.parse(unqualifyReasonsString);

    // if the lead status is unqualified, find any available reason text
    const reasonComment =
      status === LeadStatus.UNQUALIFIED && chosenReason
        ? parsedUnqualifyReasons.find((r) => r.name === chosenReason)?.commentText || ''
        : '';

    setValue(
      'leadComment',
      buildLeadComment(additionalNoteText, scriptResponseSummaryString, reasonComment),
      { shouldDirty: true }
    );
    if (dirtyFields.hasOwnProperty('additionalNote')) {
      storeInSessionStorage('additionalNote', getValues('additionalNote'));
    }
  }, [
    status,
    reasonUnqualified,
    unqualifyReasonsString,
    scriptResponseSummaryString,
    additionalNoteText,
    getValues,
    setValue,
    scriptSummaryText,
    dirtyFields,
  ]);

  useEffect(() => {
    if (isMarkedDone) {
      sessionStorage.removeItem('additionalNote');
    }
  }, [isMarkedDone]);

  useEffect(() => {
    const isStatusChangedAndValid = dirtyFields.hasOwnProperty('status') && !!status;
    dispatch(updateLeadSummaryStatus(isStatusChangedAndValid));
  }, [status, dispatch, dirtyFields]);

  const onSubmit = async (data: any) => {
    const isAdditionalNoteChange = dirtyFields.hasOwnProperty('additionalNote');
    const isCommentChange = dirtyFields.hasOwnProperty('leadComment');
    const isStatusChange = dirtyFields.hasOwnProperty('status');

    // we are performing a lead status change
    if (isStatusChange) {
      switch (data.status) {
        case LeadStatus.BAD_LEAD_TECH_SUPPORT:
          await dispatch(apiInvalidateLead(data.additionalNote ?? ''));
          break;

        case LeadStatus.NEW_LEAD:
          await dispatch(apiReopenLead(data.additionalNote ?? ''));
          break;

        case LeadStatus.QUALIFIED:
          // verify that script response intent has been completed before allowing lead to be qualified
          if (!hasQualifyAnswer) {
            dispatch(
              setNotification({
                type: 'error',
                text: 'Please complete the integrated script before qualifying',
              })
            );
            return;
          }

          await dispatch(
            apiQualifyLead({
              additionalNote: data.additionalNote ?? '',
            })
          );
          break;

        case LeadStatus.UNQUALIFIED:
          if (!data.reasonUnqualified) {
            dispatch(setNotification({ type: 'error', text: 'Please select a reason' }));
            return;
          }

          await dispatch(
            apiUnqualifyLead({
              additionalNote: data.additionalNote ?? '',
              reasonUnqualified: data.reasonUnqualified,
            })
          );
          break;
      }

      sessionStorage.removeItem('additionalNote');
      return;
    }
    // we are sending an additional note change
    if (isCommentChange || isAdditionalNoteChange) {
      await dispatch(apiPatchLead({ additionalNote: data.additionalNote ?? '' }));
      sessionStorage.removeItem('additionalNote');
    }
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, tabIdx: number) => {
    setTabIndex(tabIdx);
  };

  const filterStatusOptions = (statusOption: any) => {
    switch (leadStatus) {
      case LeadStatus.UNQUALIFIED:
      case LeadStatus.QUALIFIED:
        return statusOption.value !== LeadStatus.BAD_LEAD_TECH_SUPPORT;
      case LeadStatus.BAD_LEAD_TECH_SUPPORT:
        return (
          statusOption.value !== LeadStatus.UNQUALIFIED &&
          statusOption.value !== LeadStatus.QUALIFIED
        );
      case LeadStatus.NEW_LEAD:
        return (
          leadTag !== LeadTag.INBOUND_CALL ||
          statusOption.value !== LeadStatus.BAD_LEAD_TECH_SUPPORT
        );
      default:
        return true;
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <Stack space={theme.spacing(1)}>
        <Grid direction="row" container>
          <Grid item xs={4}>
            <Controller
              as={<TextField select />}
              name="status"
              control={control}
              SelectProps={{
                IconComponent: KeyboardArrowDownIcon,
              }}
              InputProps={{
                className: classes.formInput,
              }}
              fullWidth
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {DropDowns.leadStatuses.filter(filterStatusOptions).map((statusOption) => (
                <MenuItem key={statusOption.label} value={statusOption.value}>
                  {statusOption.label}
                </MenuItem>
              ))}
            </Controller>
          </Grid>
          <Grid item xs={8} container spacing={0}>
            <Tabs
              classes={{ indicator: classes.tabIndicator }}
              className="no-padding"
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="lead summary tabs"
              variant="fullWidth"
            >
              <StyledTab
                label="additional notes"
                classes={{ root: classes.tabRoot }}
                className={classes.tab}
              />
              <StyledTab
                label="script summary"
                classes={{ root: classes.tabRoot }}
                className={classes.tab}
              />
            </Tabs>
          </Grid>
        </Grid>
        <Controller
          as={<TextField select />}
          name="reasonUnqualified"
          control={control}
          label="reason"
          required={status === LeadStatus.UNQUALIFIED}
          className={cx(classes.formControl, 'no-margin')}
          SelectProps={{
            IconComponent: KeyboardArrowDownIcon,
          }}
          InputProps={{
            className: classes.formInput,
          }}
          fullWidth
          style={{
            display: status === LeadStatus.UNQUALIFIED ? 'block' : 'none',
          }}
          disabled={status !== LeadStatus.UNQUALIFIED}
        >
          {unqualifyReasons.map((reason: any) => (
            <MenuItem key={reason.id} value={reason.name}>
              {reason.name}
            </MenuItem>
          ))}
        </Controller>
        <TabPanel value={tabIndex} index={0}>
          <Controller
            as={<TextField />}
            name="additionalNote"
            control={control}
            multiline
            rows={10}
            fullWidth
            placeholder="Enter additional notes. These notes will be visible to the customer. Notes are intended to be short and sweet."
            variant="filled"
            InputProps={{
              className: classes.textArea,
            }}
            defaultValue=""
          />
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          <Controller
            as={<TextField />}
            name="leadComment"
            control={control}
            multiline
            rows={10}
            fullWidth
            placeholder="Script summary (Read Only)"
            variant="filled"
            InputProps={{
              className: classes.textArea,
              readOnly: true,
            }}
            defaultValue=""
          />
        </TabPanel>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isDirty || isSubmitting}
        >
          Update Lead
        </Button>
      </Stack>
    </FormContainer>
  );
};

export default LeadSummary;
